import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { CheckCircle, XCircle } from 'lucide-react';

import { AuthLayout } from '../../components/auth/AuthLayout';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { history } from '../../store/store';
import { green01 } from '../../theme';

const { unsubscribeNewsletter } = userReducer.actions;

const reasonOptions = [
  { value: 'too_frequent', label: 'Emails are too frequent' },
  { value: 'not_relevant', label: 'Content is not relevant to me' },
  { value: 'never_signed_up', label: 'I never signed up for this' },
  { value: 'privacy_concerns', label: 'Privacy concerns' },
  { value: 'other', label: 'Other (please specify)' },
];

export const NewsletterUnsubscribePage = () => {
  const [token, setToken] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const dispatch = useAppDispatch();
  const unsubscribeSuccess = useAppSelector((state) => state.user.unsubscribeSuccess);
  const unsubscribeError = useAppSelector((state) => state.user.unsubscribeError);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) setToken(tokenParam);
  }, []);

  useEffect(() => {
    if (unsubscribeSuccess || unsubscribeError) setIsProcessing(false);
  }, [unsubscribeSuccess, unsubscribeError]);

  const handleUnsubscribe = () => {
    if (token) {
      setShowConfirmation(false);
      setShowReasonForm(true);
    }
  };

  const handleUnsubscribeWithReason = () => {
    if (token) {
      setIsProcessing(true);
      setShowReasonForm(false);
      const unsubscribeReason = selectedReason === 'other' ? customReason : selectedReason;
      dispatch(unsubscribeNewsletter({ token, unsubscribeReason }));
    }
  };

  const handleCancel = () => history.push('/');

  if (showConfirmation) {
    return (
      <AuthLayout title="Unsubscribe" subtitle="Are you sure you want to unsubscribe from our newsletter?">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleUnsubscribe}
            disabled={!token}
            sx={{
              py: 1.5,
              bgcolor: '#FF8A00',
              color: 'white',
              fontWeight: 500,
              '&:hover': { bgcolor: '#e67a00' },
            }}
          >
            Yes, Unsubscribe
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCancel}
            sx={{
              py: 1.5,
              color: green01,
              borderColor: green01,
              '&:hover': { borderColor: green01, backgroundColor: 'rgba(179, 236, 73, 0.05)' },
            }}
          >
            Cancel
          </Button>
          {!token && (
            <Typography variant="body2" sx={{ color: '#FF8A00', textAlign: 'center', mt: 1 }}>
              Invalid or missing token
            </Typography>
          )}
        </Box>
      </AuthLayout>
    );
  }

  if (showReasonForm) {
    return (
      <AuthLayout title="Help us improve" subtitle="We're sorry to see you go. Could you tell us why?">
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <RadioGroup value={selectedReason} onChange={(e) => setSelectedReason(e.target.value)}>
            {reasonOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio sx={{ color: '#B3B3B3', '&.Mui-checked': { color: green01 } }} />}
                label={option.label}
                sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { color: '#B3B3B3' } }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {selectedReason === 'other' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Please tell us more..."
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: green01 },
              },
              '& .MuiInputBase-input': { color: 'white' },
            }}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setShowReasonForm(false);
              setShowConfirmation(true);
              setSelectedReason('');
              setCustomReason('');
            }}
            sx={{
              py: 1.5,
              color: '#B3B3B3',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': { borderColor: 'rgba(255, 255, 255, 0.4)' },
            }}
          >
            Back
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleUnsubscribeWithReason}
            disabled={!selectedReason || (selectedReason === 'other' && !customReason.trim())}
            sx={{
              py: 1.5,
              bgcolor: '#FF8A00',
              color: 'white',
              fontWeight: 500,
              '&:hover': { bgcolor: '#e67a00' },
              '&.Mui-disabled': { bgcolor: 'rgba(255, 138, 0, 0.3)', color: 'rgba(255,255,255,0.4)' },
            }}
          >
            Unsubscribe
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  if (isProcessing) {
    return (
      <AuthLayout title="Processing" subtitle="Please wait while we process your request.">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: green01 }} />
        </Box>
      </AuthLayout>
    );
  }

  if (unsubscribeSuccess) {
    return (
      <AuthLayout
        title="Unsubscribed"
        subtitle="You have been successfully unsubscribed."
        cardClassName="md:max-h-[90dvh]"
      >
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <CheckCircle size={64} color={green01} style={{ marginBottom: 16 }} />
          <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
            You will no longer receive our newsletter emails.
          </Typography>
        </Box>
      </AuthLayout>
    );
  }

  if (unsubscribeError) {
    return (
      <AuthLayout title="Unsubscribe Failed" subtitle="Something went wrong.">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <XCircle size={64} color="#FF8A00" style={{ marginBottom: 16 }} />
          <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
            {unsubscribeError}
          </Typography>
        </Box>
      </AuthLayout>
    );
  }

  return null;
};
